import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import ThumbDownRoundedIcon from "@mui/icons-material/ThumbDownRounded";
import ThumbUpRoundedIcon from "@mui/icons-material/ThumbUpRounded";
import { Box, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import { ReactNode } from "react";
import { MenuItemsAnalyticsSummary, PerformerRef } from "../types";

const STATS_GRID = {
  display: "grid",
  gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
  gap: 2,
};

const Stat = ({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
      {icon}
      <Typography variant="body2" color="text.secondary" noWrap>
        {label}
      </Typography>
    </Box>
    {children}
  </Box>
);

const ICON_SX = { fontSize: 18 };

const VALUE_SX = { fontSize: "20px", fontWeight: 600, lineHeight: "28px" };

const PerformerValue = ({ performer }: { performer: PerformerRef | null }) => {
  if (!performer) {
    return <Typography sx={VALUE_SX}>No orders</Typography>;
  }
  const tieCount = performer.tiedNames ? performer.tiedNames.length - 1 : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Typography sx={VALUE_SX} noWrap title={performer.name}>
        {performer.name}
      </Typography>
      {tieCount > 0 && performer.tiedNames && (
        <Tooltip title={performer.tiedNames.join(", ")}>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              px: 0.75,
              borderRadius: 1,
              bgcolor: "greyscale.200",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: "20px",
            }}
          >
            +{tieCount}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

interface SummaryCardsProps {
  summary: MenuItemsAnalyticsSummary | null;
  loading: boolean;
  error: boolean;
}

export const SummaryCards = ({ summary, loading, error }: SummaryCardsProps) => {
  const stats: { icon: ReactNode; label: string }[] = [
    { icon: <ReceiptLongRoundedIcon sx={ICON_SX} />, label: "Total Orders" },
    { icon: <GroupRoundedIcon sx={ICON_SX} />, label: "Users" },
    { icon: <BarChartRoundedIcon sx={ICON_SX} />, label: "Average Order/User" },
    { icon: <ThumbUpRoundedIcon sx={ICON_SX} />, label: "Top Performer" },
    { icon: <ThumbDownRoundedIcon sx={ICON_SX} />, label: "Low Performer" },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography sx={{ fontSize: "16px", fontWeight: 600, lineHeight: "24px" }}>
        Overview
      </Typography>

      <Box sx={STATS_GRID}>
        {loading && !summary
          ? stats.map(s => (
              <Stat key={s.label} icon={s.icon} label={s.label}>
                <Skeleton variant="text" width="60%" height={32} />
              </Stat>
            ))
          : error || !summary
          ? stats.map(s => (
              <Stat key={s.label} icon={s.icon} label={s.label}>
                <Tooltip title="Unable to load data. Please refresh.">
                  <Typography sx={VALUE_SX}>--</Typography>
                </Tooltip>
              </Stat>
            ))
          : [
              <Stat key="orders" icon={stats[0].icon} label={stats[0].label}>
                <Typography sx={VALUE_SX}>{summary.totalOrders.toLocaleString()}</Typography>
              </Stat>,
              <Stat key="users" icon={stats[1].icon} label={stats[1].label}>
                <Typography sx={VALUE_SX}>{summary.uniqueUsers.toLocaleString()}</Typography>
              </Stat>,
              <Stat key="avg" icon={stats[2].icon} label={stats[2].label}>
                <Typography sx={VALUE_SX}>{summary.avgOrdersPerAthlete.toLocaleString()}</Typography>
              </Stat>,
              <Stat key="top" icon={stats[3].icon} label={stats[3].label}>
                <PerformerValue performer={summary.topPerformer} />
              </Stat>,
              <Stat key="low" icon={stats[4].icon} label={stats[4].label}>
                <PerformerValue performer={summary.lowPerformer} />
              </Stat>,
            ]}
      </Box>
    </Paper>
  );
};
